import { useFocusEffect, useRoute } from '@react-navigation/native';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import Button from '../../components/button/Button';
import DocumentView from '../../components/documentViewer';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import FilePicker from '../../components/filePicker/filepicker.web';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import CommanModal from '../tenants/tenantOnboarding/screens/commanModal';
import OpenFlowModal from '../tenants/tenantOnboarding/atoms/modal';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import { AntDesign, Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Yes / No toggle ─────────────────────────────────────────── */
const YesNoToggle = ({ value, onChange }) => (
  <View style={yn.row}>
    <TouchableOpacity
      style={[yn.btn, value === 'Yes' && yn.btnActive]}
      onPress={() => onChange('Yes')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'Yes' && yn.txtActive]}>Yes</Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[yn.btn, value === 'No' && yn.btnActive]}
      onPress={() => onChange('No')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'No' && yn.txtActive]}>No</Text>
    </TouchableOpacity>
  </View>
);
const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: {
    width: 80, paddingVertical: 8, borderRadius: 8,
    borderWidth: 1.5, borderColor: '#D1D5DB',
    backgroundColor: '#fff', alignItems: 'center',
  },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 13, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Bought certificate status card ──────────────────────────── */
const STATUS_THEMES = {
  Active: {
    bg: '#F0FDF4',
    border: '#BBF7D0',
    badgeBg: '#DCFCE7',
    badgeText: '#15803D',
    icon: 'check-circle-outline',
    label: 'Active',
  },
  completed: {
    bg: '#F0FDF4',
    border: '#BBF7D0',
    badgeBg: '#DCFCE7',
    badgeText: '#15803D',
    icon: 'check-circle-outline',
    label: 'Completed',
  },
  pending: {
    bg: '#FFFBEB',
    border: '#FDE68A',
    badgeBg: '#FEF3C7',
    badgeText: '#B45309',
    icon: 'clock-outline',
    label: 'Pending Appointment',
  },
  Cancelled: {
    bg: '#FEF2F2',
    border: '#FECACA',
    badgeBg: '#FEE2E2',
    badgeText: '#B91C1C',
    icon: 'alert-circle-outline',
    label: 'Cancelled',
  },
};

const getStatusTheme = (status) =>
  STATUS_THEMES[status] || {
    bg: '#F9FAFB',
    border: '#E5E7EB',
    badgeBg: '#F3F4F6',
    badgeText: '#4B5563',
    icon: 'information-outline',
    label: status || 'Unknown',
  };

const BoughtCertCard = ({ cert, onManagement, onReset }) => {
  const theme = getStatusTheme(cert?.status);
  const { width } = useWindowDimensions();
  const isMobile = width <= 640;

  return (
    <View style={[bc.card, { backgroundColor: theme.bg, borderColor: theme.border }]}>
      <View style={[bc.row, isMobile && bc.rowMobile]}>
        {/* Left Info: Status Badge & Appointment Date */}
        <View style={bc.infoLeft}>
          <View style={bc.badgeRow}>
            <View style={[bc.badge, { backgroundColor: theme.badgeBg }]}>
              <MaterialCommunityIcons name={theme.icon} size={14} color={theme.badgeText} />
              <Text style={[bc.badgeText, { color: theme.badgeText }]}>
                {theme.label}
              </Text>
            </View>
          </View>

          {cert?.status !== 'completed' && (
            <View style={bc.dateRow}>
              <Ionicons name="calendar-outline" size={15} color="#6B7280" />
              <Text style={bc.dateText}>
                Appointment Date:{' '}
                <Text style={bc.dateValue}>
                  {cert?.ammendDate ? moment(cert?.ammendDate).format('YYYY/MM/DD') : 'N/A'}
                </Text>
              </Text>
            </View>
          )}
        </View>

        {/* Right Actions: Management & Reset buttons */}
        <View style={bc.btnRow}>
          <TouchableOpacity style={bc.btnManagement} onPress={onManagement} activeOpacity={0.8}>
            <MaterialCommunityIcons name="cog-outline" size={15} color="#FFFFFF" />
            <Text style={bc.btnManagementText}>Management</Text>
          </TouchableOpacity>

          <TouchableOpacity style={bc.btnReset} onPress={onReset} activeOpacity={0.8}>
            <MaterialCommunityIcons name="refresh" size={15} color="#EF4444" />
            <Text style={bc.btnResetText}>Reset</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};
const bc = StyleSheet.create({
  card: {
    marginTop: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    ...SHADOWS.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  rowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 14,
  },
  infoLeft: {
    flex: 1,
    gap: 8,
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  dateText: {
    fontSize: 13,
    color: '#4B5563',
    fontWeight: '500',
  },
  dateValue: {
    fontSize: 13,
    color: '#111827',
    fontWeight: '700',
  },
  btnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  btnManagement: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#6C2BD9',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  btnManagementText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  btnReset: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#FCA5A5',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  btnResetText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#EF4444',
  },
});

/* ─── Upload Drop Zone ────────────────────────────────────────── */
const UploadZone = ({ onDataSuccess }) => (
  <View style={uz.wrap}>
    <View style={uz.iconWrap}>
      <Ionicons name="cloud-upload-outline" size={22} color="#6C2BD9" />
    </View>
    <FilePicker
      style={{ width: '100%' }}
      onDataSuccess={onDataSuccess}
    />
    <Text style={uz.hint}>
      Drag or <Text style={uz.link}>click to upload</Text>
    </Text>
  </View>
);
const uz = StyleSheet.create({
  wrap: {
    borderWidth: 1.5, borderStyle: 'dashed', borderColor: '#c4b5fd',
    borderRadius: 10, paddingVertical: 20, paddingHorizontal: 12,
    alignItems: 'center', backgroundColor: '#faf7ff',
  },
  iconWrap: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  hint: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  link: { color: '#6C2BD9', fontWeight: '600' },
});



/* ─── Certificate Card (shared between web & mobile) ──────────── */
const CertCard = ({
  isMobile,
  iconName,
  iconColor,
  iconBg,
  title,
  description,
  validBadge,
  question,
  value,
  onToggle,
  buyButton,
  boughtCerts,
  showFields,
  expiryDate,
  onExpiryChange,
  onUpload,
  documents,
}) => (
  <View style={[cc.card, isMobile && cc.cardMobile]}>
    {/* Card header row */}
    <View style={cc.headerRow}>
      <View style={[cc.iconWrap, { backgroundColor: iconBg }]}>
        <MaterialCommunityIcons name={iconName} size={18} color={iconColor} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={cc.title} numberOfLines={1}>{title}</Text>
        <Text style={cc.desc} numberOfLines={2}>{description}</Text>
      </View>
      {validBadge ? (
        <View style={cc.validBadge}>
          <Text style={cc.validText}>{validBadge}</Text>
        </View>
      ) : null}
    </View>

    {/* Question + toggle */}
    <Text style={cc.question}>{question}</Text>
    <YesNoToggle value={value} onChange={onToggle} />

    {/* Buy button */}
    {buyButton && <View style={{ marginTop: 10 }}>{buyButton}</View>}

    {/* Bought cert cards */}
    {boughtCerts}

    {/* Expiry + upload (shown when Yes) */}
    {showFields && (
      <View style={[cc.fields, isMobile && cc.fieldsMobile]}>
        <View style={cc.fieldHalf}>
          <Text style={cc.fieldLabel}>Expiry Date</Text>
          <TextInputComponent
            onChangeText={onExpiryChange}
            isDate
            date={expiryDate}
          />
        </View>
        <View style={cc.fieldHalf}>
          <Text style={cc.fieldLabel}>Upload Documents</Text>
          <UploadZone onDataSuccess={onUpload} />
        </View>
      </View>
    )}

    {/* Uploaded documents */}
    {documents}
  </View>
);
const cc = StyleSheet.create({
  card: {
    backgroundColor: '#fff', borderRadius: 14, padding: 18,
    marginBottom: 16, ...SHADOWS.sm,
  },
  cardMobile: { padding: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  iconWrap: { width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 14, fontWeight: '700', color: '#111827' },
  desc: { fontSize: 12, color: '#9CA3AF', marginTop: 2, lineHeight: 17 },
  validBadge: { backgroundColor: '#D1FAE5', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 3, alignSelf: 'flex-start', flexShrink: 0 },
  validText: { fontSize: 11, fontWeight: '700', color: '#059669' },
  question: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 10, marginTop: 4 },
  fields: { flexDirection: 'row', gap: 16, marginTop: 16 },
  fieldsMobile: { flexDirection: 'column' },
  fieldHalf: { flex: 1 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
});

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Certifications = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [showModal, setModal] = useState({ status: false, type: '' });
  const [resetConfirm, setResetConfirm] = useState({ show: false, item: null });
  const [price, setPrice] = useState({ gas_safety_certificate: 0, epc: 0, eicr: 0, pat: 0 });
  const [gasCertificate, setGas] = useState('No');
  const [epcCertificate, setEPC] = useState('No');
  const [eicrCertificate, setEICR] = useState('No');
  const [state, setState] = useState({
    gas_certificate: [],
    gas_certificate_expiry: '',
    epc_certificate: [],
    epc_certificate_expiry: '',
    eicr_certificate: [],
    eicr_certificate_expiry: '',
    other_certificate: [],
    other_certificate_expiry: '',
    other_certificate_title: '',
    buyed_certificates: [],
    gas_buyed_certificate: [],
    epc_buyed_certificate: [],
    eicr_buyed_certificate: [],
  });

  /* ── Modal body renderers (unchanged) ── */
  const renderModalBodyforGas = (
    <CommanModal onSubmitted={() => setModal({ type: '', status: false })} type={'gas'} initialCost={price?.gas_safety_certificate} minRange={2} flowID={'flowId'} />
  );
  const renderModalBodyforEPC = (
    <CommanModal onSubmitted={() => setModal(false)} type={'epc'} initialCost={price?.epc} minRange={1} flowID={'flowId'} />
  );
  const renderModalBodyforEICR = (
    <CommanModal onSubmitted={() => setModal(false)} type={'eicr'} initialCost={price?.eicr} minRange={1} flowID={'flowId'} />
  );

  const getDynamicCards = (type) => {
    switch (type) {
      case 'gas': return { body: renderModalBodyforGas, title: 'Gas Safety Certificate (CP12)', subtitle: 'In order to faciliate the purchase of your Gas Certificate, please enter the details below' };
      case 'epc': return { body: renderModalBodyforEPC, title: 'Energy Performance Certificate (EPC)', subtitle: 'In order to faciliate the purchase of your EPC, please enter the details below' };
      case 'eicr': return { body: renderModalBodyforEICR, title: 'Electrical Installation Condition Report (EICR)', subtitle: 'In order to faciliate the purchase of your EICR, please enter the details below' };
      default: return { body: renderModalBodyforEICR, title: 'Electrical Installation', subtitle: 'In order to faciliate the purchase of your EPC, please enter the details below' };
    }
  };

  /* ── API calls (unchanged) ── */
  useFocusEffect(
    useCallback(() => {
      if (activePropertyId) _getCertifactes(activePropertyId);
    }, [activePropertyId])
  );

  const executeReset = (e) => {
    ajax.post(Urls.ResetCertificate + '/' + e.buy_certificate_id)
      .then(({ data }) => {
        if (data.status) {
          showSuccesToast('Certificate reset successfully!');
          if (activePropertyId) _getCertifactes(activePropertyId);
        } else {
          showErrorToast(data.message || 'Failed to reset certificate');
        }
      })
      .catch(() => showErrorToast('Something went wrong'));
  };

  const handleReset = (e) => setResetConfirm({ show: true, item: e });

  const _getCertifactes = (id) => {
    ajax.get(Urls.Certificates + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          setState({
            ...state,
            gas_certificate: data?.data?.allPropertyCertificates?.gas_certificate?.files?.length > 0 ? data?.data?.allPropertyCertificates?.gas_certificate?.files : [],
            gas_certificate_expiry: data?.data?.allPropertyCertificates?.gas_certificate?.expiry_date || '',
            epc_certificate: data?.data?.allPropertyCertificates?.epc_certificate?.files?.length > 0 ? data?.data?.allPropertyCertificates?.epc_certificate?.files : [],
            epc_certificate_expiry: data?.data?.allPropertyCertificates?.epc_certificate?.expiry_date || '',
            eicr_certificate: data?.data?.allPropertyCertificates?.eicr_certificate?.files?.length > 0 ? data?.data?.allPropertyCertificates?.eicr_certificate?.files : [],
            eicr_certificate_expiry: data?.data?.allPropertyCertificates?.eicr_certificate?.expiry_date || '',
            other_certificate: data?.data?.allPropertyCertificates?.other_certificate?.files?.length > 0 ? data?.data?.allPropertyCertificates?.other_certificate?.files : [],
            other_certificate_expiry: data?.data?.allPropertyCertificates?.other_certificate?.expiry_date || '',
            other_certificate_title: data?.data?.allPropertyCertificates?.other_certificate?.title,
            buyed_certificates: data?.data?.buyedCertificates?.length > 0 ? data?.data?.buyedCertificates : [],
            gas_buyed_certificate: data?.data?.buyedCertificates?.gas_certificate?.length > 0 ? data?.data?.buyedCertificates?.gas_certificate : [],
            epc_buyed_certificate: data?.data?.buyedCertificates?.epc_certificate?.length > 0 ? data?.data?.buyedCertificates?.epc_certificate : [],
            eicr_buyed_certificate: data?.data?.buyedCertificates?.eicr_certificate?.length > 0 ? data?.data?.buyedCertificates?.eicr_certificate : [],
          });
          setGas(data?.data?.allPropertyCertificates?.gas_certificate?.status === 'Yes' ? 'Yes' : 'No');
          setEPC(data?.data?.allPropertyCertificates?.epc_certificate?.status === 'Yes' ? 'Yes' : 'No');
          setEICR(data?.data?.allPropertyCertificates?.eicr_certificate?.status === 'Yes' ? 'Yes' : 'No');
        } else {
          showErrorToast(data.message);
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onSave = (navigatePage = true) => {
    const requestBody = {
      gas_certificate: { name: 'gas_certificate', files: state?.gas_certificate, expiry_date: state?.gas_certificate_expiry, status: gasCertificate },
      epc_certificate: { name: 'epc_certificate', files: state?.epc_certificate, expiry_date: state?.epc_certificate_expiry, status: epcCertificate },
      eicr_certificate: { name: 'eicr_certificate', files: state?.eicr_certificate, expiry_date: state?.eicr_certificate_expiry, status: eicrCertificate },
      other_certificate: { name: 'other_certificate', files: state?.other_certificate, title: state.title, expiry_date: state?.other_certificate_expiry || '' },
    };
    ajax.post(Urls.Certificates + '/' + activePropertyId, requestBody)
      .then(({ data }) => {
        if (data.status) {
          if (navigatePage) {
            navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
          } else {
            showSuccesToast('Certification archived successfully!');
          }
        } else {
          showErrorToast(data.message?.includes('Invalid date') ? 'Expire date is required' : data.message);
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onAddCertifcate = (value, tag) => {
    let obj = {
      name: tag,
      document_url: value[0]?.openbrixName,
      file_name: tag === 'other_certificate' ? state?.other_certificate_title : value[0]?.name,
    };
    setState({ ...state, [tag]: [...state[tag], obj], other_certificate_title: '' });
  };

  const moveToDocs = (data) => {
    ajax.post(Urls.MoveToDocs + '/' + activePropertyId, data)
      .then(({ data }) => {
        if (data.status) showSuccesToast('Document moved to myDocs section!');
        else showErrorToast('Something went wrong!');
      })
      .catch(() => showErrorToast('Something went wrong!'));
  };

  const _onRemove = (obj, tag, i, ismoveToDocs) => {
    let arr = state[tag];
    if (ismoveToDocs) {
      moveToDocs(arr[i]);
      arr.splice(i, 1);
      setState({ ...state, [tag]: arr });
    } else {
      arr[i].is_deleted = true;
      setState({ ...state, [tag]: arr });
      _onSave(false);
    }
  };

  /* ── Helpers ── */
  const renderDocs = (certArray, tag) =>
    certArray
      ?.filter((e) => !e?.is_deleted)
      .map((e, i) => (
        <DocumentView
          key={i}
          title={e?.file_name || e?.name}
          url={e?.document_url}
          onDelete={(moveToDocs) => _onRemove(e, tag, i, moveToDocs)}
        />
      ));

  const renderBoughtCerts = (certArray) =>
    certArray
      ?.filter((e) => e?.status !== 'closed')
      .map((e, i) => (
        <BoughtCertCard
          key={i}
          cert={e}
          onManagement={() => window.open(e?.management_link)}
          onReset={() => handleReset(e)}
        />
      ));

  /* ── Reset Confirm Modal (unchanged logic) ── */
  const resetModal = (
    <OpenFlowModal
      showModal={resetConfirm.show}
      setShowModal={(val) => setResetConfirm({ show: val, item: null })}
      modalHeading={'Reset Certificate'}
      modalSubHeading={'You have already been charged for this certificate. Should you cancel this request, you will not receive a refund.'}
      modalBody={
        <View style={{ marginTop: 16 }}>
          <Text style={{ fontSize: 14, color: '#555', marginBottom: 20 }}>
            Do you wish to proceed? This action cannot be undone!
          </Text>
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
            <Button type="solid" childText="No, Cancel" width={110} color="#888" onPress={() => setResetConfirm({ show: false, item: null })} />
            <Button type="solid" childText="Yes, Reset" width={110} color="#FC4850" onPress={() => { setResetConfirm({ show: false, item: null }); executeReset(resetConfirm.item); }} />
          </View>
        </View>
      }
      showFooter={false}
    />
  );

  /* ── BUY buttons ── */
  const gasBuyBtn = state?.gas_buyed_certificate?.length > 0 ? null : (
    <Button type="solid" childText="BUY" color={'#006fcf'} onPress={() => {
      setModal({ type: 'gas', status: true });
      navigation.navigate('CommonBuyCertificate', { typee: { title: getDynamicCards('gas').title }, status: true, pageType: 'gas_certificate', property_id: route?.params?.id });
    }} />
  );
  const epcBuyBtn = state?.epc_buyed_certificate?.length > 0 ? null : (
    <Button type="solid" childText="BUY" color={'#006fcf'} onPress={() => {
      setModal({ type: 'epc', status: true });
      navigation.navigate('CommonBuyCertificate', { typee: { title: getDynamicCards('epc').title }, status: true, pageType: 'epc_certificate', property_id: route?.params?.id });
    }} />
  );
  const eicrBuyBtn = state?.eicr_buyed_certificate?.length > 0 ? null : (
    <Button type="solid" childText="BUY" color={'#006fcf'} onPress={() => {
      setModal({ type: 'eicr', status: true });
      navigation.navigate('CommonBuyCertificate', { typee: { title: getDynamicCards('eicr').title }, status: true, pageType: 'eicr_certificate', property_id: route?.params?.id });
    }} />
  );

  /* ═══════════════════════════════════
     MOBILE LAYOUT
  ═══════════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        {resetModal}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >

          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <MaterialCommunityIcons name="shield-check-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Certification</Text>
              <Text style={ms.headerSub}>Please fill in the certification details for this property</Text>
            </View>
          </View>

          {/* Mark as Complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              label={'Certification requirements'}
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2}
            />
          </View>

          {/* GAS Safety */}
          <CertCard
            isMobile
            iconName="fire"
            iconColor="#6C2BD9"
            iconBg="#F0E8FF"
            title="Gas Safety Certificate"
            description="Annual certificate required for all gas appliances."
            validBadge="VALID"
            question="Do you have an in-date Gas Safety Certificate?"
            value={gasCertificate}
            onToggle={(e) => {
              if (e === 'No') setState({ ...state, gas_certificate: [], gas_certificate_expiry: '' });
              setGas(e);
            }}
            buyButton={gasBuyBtn}
            boughtCerts={renderBoughtCerts(state.gas_buyed_certificate)}
            showFields={gasCertificate === 'Yes'}
            expiryDate={state.gas_certificate_expiry}
            onExpiryChange={(e) => setState({ ...state, gas_certificate_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'gas_certificate')}
            documents={renderDocs(state.gas_certificate, 'gas_certificate')}
          />

          {/* EPC */}
          <CertCard
            isMobile
            iconName="lightning-bolt"
            iconColor="#F59E0B"
            iconBg="#FEF3C7"
            title="Energy Performance Certificate (EPC)"
            description="Rates energy efficiency of the property from A to G."
            validBadge="VALID"
            question="Do you have an in-date Energy Performance Certificate?"
            value={epcCertificate}
            onToggle={(e) => {
              if (e === 'No') setState({ ...state, epc_certificate: [], epc_certificate_expiry: '' });
              setEPC(e);
            }}
            buyButton={epcBuyBtn}
            boughtCerts={renderBoughtCerts(state.epc_buyed_certificate)}
            showFields={epcCertificate === 'Yes'}
            expiryDate={state.epc_certificate_expiry}
            onExpiryChange={(e) => setState({ ...state, epc_certificate_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'epc_certificate')}
            documents={renderDocs(state.epc_certificate, 'epc_certificate')}
          />

          {/* EICR */}
          <CertCard
            isMobile
            iconName="flash"
            iconColor="#3B82F6"
            iconBg="#DBEAFE"
            title="Electrical Installation Condition Report (EICR)"
            description="Electrical safety inspection for rental properties."
            question="Do you have an up to date Electrical Inspection Condition Report (EICR)?"
            value={eicrCertificate}
            onToggle={(e) => {
              if (e === 'No') setState({ ...state, eicr_certificate: [], eicr_certificate_expiry: '' });
              setEICR(e);
            }}
            buyButton={eicrBuyBtn}
            boughtCerts={renderBoughtCerts(state.eicr_buyed_certificate)}
            showFields={eicrCertificate === 'Yes'}
            expiryDate={state.eicr_certificate_expiry}
            onExpiryChange={(e) => setState({ ...state, eicr_certificate_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'eicr_certificate')}
            documents={renderDocs(state.eicr_certificate, 'eicr_certificate')}
          />

          {/* Other Certificate */}
          <View style={ms.otherCard}>
            <Text style={ms.otherTitle}>Other Certificate</Text>
            <Text style={ms.fieldLabel}>Certificate title</Text>
            <TextInput
              style={ms.input}
              placeholder="Enter certificate title"
              placeholderTextColor="#b0b0b0"
              value={state.other_certificate_title}
              onChangeText={(e) => setState({ ...state, other_certificate_title: e, title: e })}
              maxLength={50}
            />
            {state.other_certificate_title?.length > 0 && (
              <Text style={ms.charLeft}>{50 - state.other_certificate_title.length} characters left</Text>
            )}
            <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Expiry Date *</Text>
            <TextInputComponent
              onChangeText={(e) => setState({ ...state, other_certificate_expiry: e })}
              isDate
              minDate={new Date()}
              date={state.other_certificate_expiry}
            />
            <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Upload Documents</Text>
            <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'other_certificate')} />
            {renderDocs(state.other_certificate, 'other_certificate')}
          </View>

          <View style={{ height: 100 }} />
        </ScrollView>

        {/* Sticky footer */}
        {/* <View style={ms.footer}>
          <Text style={ms.footerNote}>All fields marked * are required</Text>
          <TouchableOpacity style={ms.footerBtn} onPress={() => _onSave()} activeOpacity={0.85}>
            <Text style={ms.footerBtnText}>Save and Continue →</Text>
          </TouchableOpacity>
        </View> */}
        <PropertyStickyFooter onSave={() => _onSave()} isMobile={isMobile} />
      </View>
    );
  }

  /* ═══════════════════════════════════
     WEB / TABLET LAYOUT
  ═══════════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      {resetModal}

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Certification"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="shield-checkmark-outline"
          title="Certification"
          subtitle="Please fill in the certification details for this property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Certificates'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body */}
        <View style={ws.body}>
          {/* Main Card */}
          <View style={ws.mainCard}>
            {/* GAS Safety */}
            <CertCard
              isMobile={false}
              iconName="fire"
              iconColor="#6C2BD9"
              iconBg="#F0E8FF"
              title="Gas Safety Certificate"
              description="Annual certificate required for all gas appliances."
              validBadge="VALID"
              question="Do you have an in-date Gas Safety Certificate?"
              value={gasCertificate}
              onToggle={(e) => {
                if (e === 'No') setState({ ...state, gas_certificate: [], gas_certificate_expiry: '' });
                setGas(e);
              }}
              buyButton={gasBuyBtn}
              boughtCerts={renderBoughtCerts(state.gas_buyed_certificate)}
              showFields={gasCertificate === 'Yes'}
              expiryDate={state.gas_certificate_expiry}
              onExpiryChange={(e) => setState({ ...state, gas_certificate_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'gas_certificate')}
              documents={renderDocs(state.gas_certificate, 'gas_certificate')}
            />

            {/* EPC */}
            <CertCard
              isMobile={false}
              iconName="lightning-bolt"
              iconColor="#F59E0B"
              iconBg="#FEF3C7"
              title="Energy Performance Certificate (EPC)"
              description="Rates energy efficiency of the property from A to G."
              validBadge="VALID"
              question="Do you have an in-date Energy Performance Certificate (EPC)?"
              value={epcCertificate}
              onToggle={(e) => {
                if (e === 'No') setState({ ...state, epc_certificate: [], epc_certificate_expiry: '' });
                setEPC(e);
              }}
              buyButton={epcBuyBtn}
              boughtCerts={renderBoughtCerts(state.epc_buyed_certificate)}
              showFields={epcCertificate === 'Yes'}
              expiryDate={state.epc_certificate_expiry}
              onExpiryChange={(e) => setState({ ...state, epc_certificate_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'epc_certificate')}
              documents={renderDocs(state.epc_certificate, 'epc_certificate')}
            />

            {/* EICR */}
            <CertCard
              isMobile={false}
              iconName="flash"
              iconColor="#3B82F6"
              iconBg="#DBEAFE"
              title="Electrical Installation Condition Report (EICR)"
              description="Electrical safety inspection for rental properties."
              question="Do you have an up to date Electrical Inspection Condition Report (EICR)?"
              value={eicrCertificate}
              onToggle={(e) => {
                if (e === 'No') setState({ ...state, eicr_certificate: [], eicr_certificate_expiry: '' });
                setEICR(e);
              }}
              buyButton={eicrBuyBtn}
              boughtCerts={renderBoughtCerts(state.eicr_buyed_certificate)}
              showFields={eicrCertificate === 'Yes'}
              expiryDate={state.eicr_certificate_expiry}
              onExpiryChange={(e) => setState({ ...state, eicr_certificate_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'eicr_certificate')}
              documents={renderDocs(state.eicr_certificate, 'eicr_certificate')}
            />

            {/* Other Certificate section */}
            <View style={ws.otherSection}>
              <Text style={ws.otherTitle}>Other Certificate</Text>
              <View style={ws.otherGrid}>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Certificate title</Text>
                  <TextInput
                    style={ws.input}
                    placeholder="Enter certificate title"
                    placeholderTextColor="#b0b0b0"
                    value={state.other_certificate_title}
                    onChangeText={(e) => setState({ ...state, other_certificate_title: e, title: e })}
                    maxLength={50}
                  />
                  {state.other_certificate_title?.length > 0 && (
                    <Text style={ws.charLeft}>{50 - state.other_certificate_title.length} characters left</Text>
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Expiry Date *</Text>
                  <TextInputComponent
                    onChangeText={(e) => setState({ ...state, other_certificate_expiry: e })}
                    isDate
                    minDate={new Date()}
                    date={state.other_certificate_expiry}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Upload Documents</Text>
                  <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'other_certificate')} />
                </View>
              </View>
              {renderDocs(state.other_certificate, 'other_certificate')}
            </View>
          </View>

          {/* Sidebar */}
          <SetupSidebar
            activeKey="certification"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter onSave={() => _onSave()} isMobile={isMobile} />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  progressCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, ...SHADOWS.sm },
  progressRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressStep: { fontSize: 12, color: '#6C2BD9', fontWeight: '700' },
  track: { height: 6, backgroundColor: '#F0E8FF', borderRadius: 3, overflow: 'hidden' },
  fill: { width: '50%', height: '100%', backgroundColor: '#6C2BD9', borderRadius: 3 },

  headerCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, ...SHADOWS.sm },
  headerIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  completeCard: { backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 16, paddingVertical: 4, marginBottom: 12, ...SHADOWS.sm },

  otherCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, ...SHADOWS.sm },
  otherTitle: { fontSize: 15, fontWeight: '700', color: '#111827', marginBottom: 14 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#fff' },
  charLeft: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 4 },

  footer: { backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', padding: 16, ...SHADOWS.md },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingVertical: 14, alignItems: 'center' },
  footerBtnText: { color: '#fff', fontSize: 15, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  breadcrumbBar: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 14, flexWrap: 'wrap' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  body: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, paddingHorizontal: 24, paddingBottom: 100 },

  mainCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 24, ...SHADOWS.md },

  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexWrap: 'wrap' },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  otherSection: { marginTop: 8, paddingTop: 20, borderTopWidth: 1, borderTopColor: '#F3F4F6' },
  otherTitle: { fontSize: 16, fontWeight: '700', color: '#111827', marginBottom: 16 },
  otherGrid: { flexDirection: 'row', gap: 16, flexWrap: 'wrap' },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#fff' },
  charLeft: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 4 },

  stickyFooter: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB',
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 24, paddingVertical: 14, zIndex: 1000, ...SHADOWS.md,
  },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: Certifications,
  showBackButton: true,
  noScroll: true,
});
